import type { SLBPosition, InventoryItem } from '@/types';

export interface FilterMetadataResult {
  accountIds: string[];
  counterparties: string[];
  currencies: string[];
  exchanges: string[];
  caStatuses: string[];
  caTypes: string[];
  brokerAccountNames: string[];
  brokerSettlementCounterparties: string[];
  quantityRange: { min: number; max: number };
  settledQuantityRange: { min: number; max: number };
  quantityT1Range: { min: number; max: number };
  quantityT2Range: { min: number; max: number };
  quantityTxRange: { min: number; max: number };
  leverageRateRange: { min: number; max: number };
  valueEURRange: { min: number; max: number };
  currencyBenchmarkRange: { min: number; max: number };
  dailyPotentialRange: { min: number; max: number };
  adtv30DRange: { min: number; max: number };
  adtvMultipleRange: { min: number; max: number };
  adtvAdjustedRange: { min: number; max: number };
  benchmarkFeeRange: { min: number; max: number };
  activeUtilisationRange: { min: number; max: number };
  quantityOnLoanRange: { min: number; max: number };
  rebateRateRange: { min: number; max: number };
  l2cpCounterparties: string[];
  l2cpQuantityRange: { min: number; max: number };
  l2cpAmountEurRange: { min: number; max: number };
  l2cpRateRange: { min: number; max: number };
  l2cpGrossRateRange: { min: number; max: number };
  l2cpSnpDeltaRange: { min: number; max: number };
  pendingTransfersOutRange: { min: number; max: number };
  cimbEq2QuantityRange: { min: number; max: number };
  cimbSfQuantityRange: { min: number; max: number };
  cimbSf3QuantityRange: { min: number; max: number };
  velocityLQuantityRange: { min: number; max: number };
  velocityMQuantityRange: { min: number; max: number };
  exDateMin: string;
  exDateMax: string;
}

const DEFAULT_METADATA: FilterMetadataResult = {
  accountIds: [],
  counterparties: [],
  currencies: [],
  exchanges: [],
  caStatuses: [],
  caTypes: [],
  brokerAccountNames: [],
  brokerSettlementCounterparties: [],
  quantityRange: { min: 0, max: 1000000 },
  settledQuantityRange: { min: 0, max: 1000000 },
  quantityT1Range: { min: 0, max: 1000000 },
  quantityT2Range: { min: 0, max: 1000000 },
  quantityTxRange: { min: 0, max: 1000000 },
  leverageRateRange: { min: 0, max: 100 },
  valueEURRange: { min: 0, max: 1000000 },
  currencyBenchmarkRange: { min: 0, max: 100 },
  dailyPotentialRange: { min: 0, max: 10000 },
  adtv30DRange: { min: 0, max: 1000000 },
  adtvMultipleRange: { min: 0, max: 100 },
  adtvAdjustedRange: { min: 0, max: 10000 },
  benchmarkFeeRange: { min: 0, max: 100 },
  activeUtilisationRange: { min: 0, max: 100 },
  quantityOnLoanRange: { min: 0, max: 1000000 },
  rebateRateRange: { min: -100, max: 100 },
  l2cpCounterparties: [],
  l2cpQuantityRange: { min: 0, max: 10000000 },
  l2cpAmountEurRange: { min: 0, max: 100000000 },
  l2cpRateRange: { min: -100, max: 100 },
  l2cpGrossRateRange: { min: -100, max: 100 },
  l2cpSnpDeltaRange: { min: -100, max: 100 },
  pendingTransfersOutRange: { min: 0, max: 1000000 },
  cimbEq2QuantityRange: { min: 0, max: 1000000 },
  cimbSfQuantityRange: { min: 0, max: 1000000 },
  cimbSf3QuantityRange: { min: 0, max: 1000000 },
  velocityLQuantityRange: { min: 0, max: 1000000 },
  velocityMQuantityRange: { min: 0, max: 1000000 },
  exDateMin: '',
  exDateMax: '',
};


export function computeFilterMetadata(
  positions: SLBPosition[],
  rawInventoryData: InventoryItem[] | undefined
): FilterMetadataResult {
  if (positions.length === 0) return DEFAULT_METADATA;

  // Single-pass collection of unique values and min/max ranges
  const accountIdSet = new Set<string>();
  const counterpartySet = new Set<string>();
  const currencySet = new Set<string>();
  const exchangeSet = new Set<string>();
  const brokerAccountNameSet = new Set<string>();
  const brokerSettlementCpSet = new Set<string>();

  let exDateMin = '', exDateMax = '';

  type Range = { min: number; max: number };
  const mkRange = (): Range => ({ min: Infinity, max: -Infinity });

  const updateRange = (v: number | null | undefined, r: Range) => {
    if (v != null && !isNaN(v) && isFinite(v)) {
      if (v < r.min) r.min = v;
      if (v > r.max) r.max = v;
    }
  };

  const r = {
    quantity: mkRange(), settledQuantity: mkRange(), quantityT1: mkRange(), quantityT2: mkRange(), quantityTx: mkRange(),
    leverageRate: mkRange(), valueEUR: mkRange(), currencyBenchmark: mkRange(),
    dailyPotential: mkRange(), adtv30D: mkRange(), adtvMultiple: mkRange(),
    adtvAdjusted: mkRange(), benchmarkFee: mkRange(), activeUtilisation: mkRange(),
    quantityOnLoan: mkRange(), rebateRate: mkRange(),
    l2cpQuantity: mkRange(), l2cpAmountEur: mkRange(), l2cpRate: mkRange(),
    l2cpGrossRate: mkRange(), l2cpSnpDelta: mkRange(),
    pendingTransfers: mkRange(), cimbEq2: mkRange(), cimbSf: mkRange(), cimbSf3: mkRange(),
    velocityL: mkRange(), velocityM: mkRange(),
  };

  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    accountIdSet.add(p.accountId);
    counterpartySet.add(p.counterparty);
    currencySet.add(p.currency);
    // Exchange can be blank on some rows (backend default '') — skip empties so the
    // filter never offers an unselectable blank option (mirrors brokerAccountName).
    if (p.exchange) exchangeSet.add(p.exchange);
    if (p.brokerAccountName) brokerAccountNameSet.add(p.brokerAccountName);
    if (p.brokerSettlementCounterparty) brokerSettlementCpSet.add(p.brokerSettlementCounterparty);

    updateRange(p.quantity, r.quantity);
    updateRange(p.settledQuantity, r.settledQuantity);
    updateRange(p.quantityT1, r.quantityT1);
    updateRange(p.quantityT2, r.quantityT2);
    updateRange(p.quantityTx, r.quantityTx);
    updateRange(p.leverageRate, r.leverageRate);
    updateRange(p.valueEUR, r.valueEUR);
    const cbVal = parseFloat(p.currencyBenchmark || '0');
    updateRange(isNaN(cbVal) ? null : cbVal, r.currencyBenchmark);
    updateRange(p.dailyPotentialEUR, r.dailyPotential);
    updateRange(p.adtv30D, r.adtv30D);
    updateRange(p.adtvMultiple, r.adtvMultiple);
    updateRange(p.adtvAdjustedPotentialEUR, r.adtvAdjusted);
    updateRange(p.benchmarkFee, r.benchmarkFee);
    updateRange(p.activeUtilisation, r.activeUtilisation);
    updateRange(p.quantityOnLoan, r.quantityOnLoan);
    updateRange(p.rebateRate, r.rebateRate);
    updateRange(p.l2cpQuantity, r.l2cpQuantity);
    updateRange(p.l2cpAmountEur, r.l2cpAmountEur);
    updateRange(p.l2cpRate, r.l2cpRate);
    updateRange(p.l2cpGrossRate, r.l2cpGrossRate);
    updateRange(p.l2cpSnpDelta, r.l2cpSnpDelta);
    updateRange(p.pendingTransfers, r.pendingTransfers);
    updateRange(p.cimbEq2Quantity, r.cimbEq2);
    updateRange(p.cimbSfQuantity, r.cimbSf);
    updateRange(p.cimbSf3Quantity, r.cimbSf3);
    updateRange(p.velocityLQuantity, r.velocityL);
    updateRange(p.velocityMQuantity, r.velocityM);

    if (typeof p.exDate === 'string' && p.exDate.length > 0) {
      if (!exDateMin || p.exDate < exDateMin) exDateMin = p.exDate;
      if (!exDateMax || p.exDate > exDateMax) exDateMax = p.exDate;
    }
  }

  const toRange = (r: { min: number; max: number }, defMin: number, defMax: number) =>
    r.min === Infinity ? { min: defMin, max: defMax } : { min: r.min, max: r.max };

  const accountIds = Array.from(accountIdSet).sort();
  const counterparties = Array.from(counterpartySet).sort();
  const currencies = Array.from(currencySet).sort();
  const exchanges = Array.from(exchangeSet).sort();
  const brokerAccountNames = Array.from(brokerAccountNameSet).sort();
  const brokerSettlementCounterparties = Array.from(brokerSettlementCpSet).sort();

  const allCAStatuses = rawInventoryData
    ? Array.from(new Set(
        rawInventoryData
          .flatMap((item) => {
            if (!item.ca_status) return [];
            return item.ca_status.split(',').map(s => s.trim());
          })
          .filter((s) => {
            if (!s) return false;
            const normalized = s.toLowerCase();
            return normalized !== 'deleted' && normalized !== 'event error';
          })
      )).sort()
    : [];

  const allCATypes = rawInventoryData
    ? Array.from(new Set(
        rawInventoryData
          .map((item) => item.ca_type?.trim())
          .filter((s): s is string => !!s)
      )).sort()
    : [];

  return {
    accountIds,
    counterparties,
    currencies,
    exchanges,
    caStatuses: allCAStatuses,
    caTypes: allCATypes,
    brokerAccountNames,
    brokerSettlementCounterparties,
    l2cpCounterparties: counterparties,
    quantityRange: toRange(r.quantity, 0, 1000000),
    settledQuantityRange: toRange(r.settledQuantity, 0, 1000000),
    quantityT1Range: toRange(r.quantityT1, 0, 1000000),
    quantityT2Range: toRange(r.quantityT2, 0, 1000000),
    quantityTxRange: toRange(r.quantityTx, 0, 1000000),
    leverageRateRange: toRange(r.leverageRate, 0, 100),
    valueEURRange: toRange(r.valueEUR, 0, 1000000),
    currencyBenchmarkRange: toRange(r.currencyBenchmark, 0, 100),
    dailyPotentialRange: toRange(r.dailyPotential, 0, 10000),
    adtv30DRange: toRange(r.adtv30D, 0, 1000000),
    adtvMultipleRange: toRange(r.adtvMultiple, 0, 100),
    adtvAdjustedRange: toRange(r.adtvAdjusted, 0, 10000),
    benchmarkFeeRange: toRange(r.benchmarkFee, 0, 100),
    activeUtilisationRange: toRange(r.activeUtilisation, 0, 100),
    quantityOnLoanRange: toRange(r.quantityOnLoan, 0, 1000000),
    rebateRateRange: toRange(r.rebateRate, -100, 100),
    l2cpQuantityRange: toRange(r.l2cpQuantity, 0, 10000000),
    l2cpAmountEurRange: toRange(r.l2cpAmountEur, 0, 100000000),
    l2cpRateRange: toRange(r.l2cpRate, -100, 100),
    l2cpGrossRateRange: toRange(r.l2cpGrossRate, -100, 100),
    l2cpSnpDeltaRange: toRange(r.l2cpSnpDelta, -100, 100),
    pendingTransfersOutRange: toRange(r.pendingTransfers, 0, 1000000),
    cimbEq2QuantityRange: toRange(r.cimbEq2, 0, 1000000),
    cimbSfQuantityRange: toRange(r.cimbSf, 0, 1000000),
    cimbSf3QuantityRange: toRange(r.cimbSf3, 0, 1000000),
    velocityLQuantityRange: toRange(r.velocityL, 0, 1000000),
    velocityMQuantityRange: toRange(r.velocityM, 0, 1000000),
    exDateMin,
    exDateMax,
  };
}
